import React, { useEffect } from "react";
import { H1, H2, P, Span } from "../../components/Text/Text";
import { deliverySplash } from "../../assets/images";
import Button, { ButtonIntentE, ButtonModeE } from "../../components/Button/Button";
import DeliverySidebar from "../../components/DeliverySidebar/DeliverySidebar";
import { useCampaignWizard } from "../../popups/CampaignWizard/useCampaignWizard";
import {
  AccessLevelE,
  CampaignWizardMode,
  DeliveryOrderStatusE,
  ProjectStatusE,
  StatusE,
  UserRoleE,
} from "../../models/enums";
import usePopup from "../../utils/hooks/usePopup";
import FindCampaign from "../../popups/FindCampaign";
import ReDeliveryBuilder from "../../popups/ReDelivery/ReDeliveryBuilder";
import reDeliveryStyles from "../../popups/ReDelivery/reDelivery.module.scss";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setDeliveryServiceStep } from "../../store/slices/appSlice";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import { Link, useNavigate } from "react-router-dom";
import { RoutesE } from "../../routes/routesData";
import { useNonDesktopViewport } from "../../utils/hooks/useNonDesktopViewport";
import { useGetUnfinishedProjectsQuery } from "../../api/projectsApi";
import { ProjectT } from "../../models/models";
import getRoute from "./helpers/getRoute";
import { UnfinishedDeliveryT } from "./models";
import getStatusColor from "./helpers/getStatusColor";
import Tooltip from "../../components/Tooltip/Tooltip";
import getStatusText from "./helpers/getStatusText";

import styles from "./deliveryServiceIndex.module.scss";
import Loader from "../../components/Loader/Loader";

const DeliveryServiceIndex = () => {
  const isNonDesktop = useNonDesktopViewport();
  const { createCampaignWizard } = useCampaignWizard();
  const { createPopup } = usePopup();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const navigate = useNavigate();
  const { data: unfinishedProjects, isFetching } =
    useGetUnfinishedProjectsQuery(
      {},
      {
        skip: user?.accessLevel !== AccessLevelE.DeliveryServicePrivate,
      }
    );

  useEffect(() => {
    dispatch(setDeliveryServiceStep(1));
  }, []);

  useEffect(() => {
    if (user?.role !== UserRoleE.DeliveryService) {
      navigate(RoutesE.HOME);
    }
  }, [user?.role]);

  const handleClickNewCampaign = () => {
    createCampaignWizard("", CampaignWizardMode.CREATE, StatusE.Opened);
  };

  const handleFindCampaign = () => {
    createPopup(FindCampaign);
  };

  const handleReusePreviousDelivery = () => {
    createPopup({
      body: <ReDeliveryBuilder entryType="JumboDelivery" />,
      className: reDeliveryStyles.popupWrapper,
    });
  };

  return (
    <>
      {!isNonDesktop && <DeliverySidebar />}
      <main className={styles.mainWrapper}>
        <H1>Welcome to JUMBOmedia's Ads delivery services system!</H1>
        <P className={styles.deliveryServiceSubheader}>
          Dear clients, the delivery of media files to broadcasters is handled
          independently and straightforwardly, following the progress steps. To
          begin, click the Create a new campaign button. If you need to deliver
          additional versions to an existing campaign or reuse an old delivery ad
          in a new campaign, click the relevant button.
        </P>
        <div className={styles.buttonsWrapper}>
          <Button
            icon="add"
            intent={ButtonIntentE.CAMPAIGN}
            className={styles.newCampaignButton}
            onClick={handleClickNewCampaign}
          >
            Create new campaign
          </Button>
          <Span className={styles.orSeparator}>or</Span>
          <div className={styles.secondaryButtonsRow}>
            <Button
              className={styles.newCampaignButton}
              onClick={handleFindCampaign}
              mode={ButtonModeE.OUTLINED}
            >
              Choose existing campaign
            </Button>
            <Button
              className={styles.newCampaignButton}
              onClick={handleReusePreviousDelivery}
              mode={ButtonModeE.OUTLINED}
            >
              Reuse previous delivery
            </Button>
          </div>
        </div>
        {isFetching && <Loader />}
        {user?.accessLevel === AccessLevelE.DeliveryServicePrivate &&
          !!unfinishedProjects?.length && (
            <div className={styles.notFinishedDeliveriesSection}>
              <H2>Continue from where you left things open</H2>
              <P>
                There are previous deliveries in your account for which the
                process has not yet been completed.
              </P>
              <P>
                You can continue the delivery process exactly as you left it.
              </P>
              <ul className={styles.unfinishedProjectList}>
                {unfinishedProjects.map((item: UnfinishedDeliveryT) => (
                  <Tooltip key={item.Id} text={getStatusText(item)}>
                    <li>
                      <span
                        className={styles.dot}
                        style={{ backgroundColor: getStatusColor(item) }}
                      />
                      {/*@ts-ignore*/}
                      <Link to={getRoute(item)}>{item?.Id} - {item?.campaignName}</Link>
                    </li>
                  </Tooltip>
                ))}
              </ul>
            </div>
          )}
        <div className={styles.splashWrapper}>
          <img src={deliverySplash} alt="delivery splash" />
        </div>
      </main>
    </>
  );
};

export default DeliveryServiceIndex;
